import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import { getNotificationVisual, TONE_ICON_CLASSES } from '../notification-visuals';
import type { INotification } from '../types';
import { NotificationIcon } from './NotificationIcon';

interface NotificationToastProps {
    notification: INotification;
    onClick: () => void;
    onClose: () => void;
}

/**
 * The card rendered inside a Sonner custom toast when a notification arrives.
 * Mirrors `NotificationRow`'s tone icon-chip + title/body so it reads as the
 * same notification "arriving" on screen.
 */
export function NotificationToast({ notification, onClick, onClose }: NotificationToastProps) {
    const { t } = useTranslation();
    const { title, body, actionUrl, category, metadata } = notification;
    const { tone } = getNotificationVisual(category, metadata);

    return (
        <div
            className={cn(
                'relative flex w-[356px] gap-3 rounded-lg border border-border-default bg-bg-surface p-3 shadow-lg',
                actionUrl && 'cursor-pointer',
            )}
            role={actionUrl ? 'button' : undefined}
            onClick={actionUrl ? onClick : undefined}
        >
            <div
                className={cn(
                    'flex size-9 shrink-0 items-center justify-center rounded-md border',
                    TONE_ICON_CLASSES[tone],
                )}
            >
                <NotificationIcon category={category} metadata={metadata} className="size-5" />
            </div>
            <div className="min-w-0 flex-1 pr-4">
                <p className="truncate text-sm font-semibold text-foreground">{title}</p>
                <p className="mt-0.5 line-clamp-2 whitespace-pre-line text-[13px] leading-snug text-muted-foreground">
                    {body}
                </p>
            </div>
            <button
                type="button"
                aria-label={t('common.dismiss', 'Dismiss')}
                className="absolute right-1.5 top-1.5 flex size-6 items-center justify-center rounded-md text-muted-foreground/70 transition-colors hover:bg-muted hover:text-foreground"
                onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                }}
            >
                <NotificationIcon name="X" className="size-3.5" />
            </button>
        </div>
    );
}
